import { Injectable } from '@angular/core';
import { OPERATORS } from '../constants';
import type { OperatorStrategy } from './operator-strategy';
import { GeosatOperatorStrategy } from './strategies/geosat.operator-strategy';
import { IceyeOperatorStrategy } from './strategies/iceye.operator-strategy';
import { WeatherexOperatorStrategy } from './strategies/weatherex.operator-strategy';

@Injectable({
  providedIn: 'root',
})
export class OperatorStrategyFactory {
  private readonly cache = new Map<string, OperatorStrategy>();

  /**
   * Strategy migration notes (GEOSAT-first):
   *
   * - This factory intentionally returns a strategy ONLY for operators that have been migrated.
   *   Everything else falls back to the existing in-component logic.
   * - To migrate the next operator:
   *   1) Add a new `...OperatorStrategy` class under `./strategies/`.
   *   2) Implement the minimal `OperatorStrategy` surface (`calculateUnitCost` today).
   *   3) Add a branch in `getStrategy()` mapping `OPERATORS.X` -> new strategy.
   *   4) Add a focused unit test for the strategy.
   *
   * This keeps refactors incremental and low-risk.
   */

  /**
   * GEOSAT-first: returns a strategy only for operators that have been migrated.
   * Others return null and fall back to legacy in-component logic.
   */
  getStrategy(operatorKey: string | undefined | null): OperatorStrategy | null {
    if (!operatorKey) return null;

    if (operatorKey === OPERATORS.GEOSAT) {
      const cached = this.cache.get(operatorKey);
      if (cached) return cached;
      const created = new GeosatOperatorStrategy();
      this.cache.set(operatorKey, created);
      return created;
    }

    if (operatorKey === OPERATORS.ICEYE) {
      const cached = this.cache.get(operatorKey);
      if (cached) return cached;
      const created = new IceyeOperatorStrategy();
      this.cache.set(operatorKey, created);
      return created;
    }

    if (operatorKey === OPERATORS.WEATHEREX) {
      const cached = this.cache.get(operatorKey);
      if (cached) return cached;
      const created = new WeatherexOperatorStrategy();
      this.cache.set(operatorKey, created);
      return created;
    }

    // Next migrations (examples):
    // if (operatorKey === OPERATORS.SATELLITEVU_VANZYL) return new SatelliteVuOperatorStrategy();
    // if (operatorKey === OPERATORS.STE) return new SteOperatorStrategy();

    return null;
  }
}
